import React, { useState, useEffect } from 'react';
import { Crown, Plus, Pencil, Trash2, GripVertical, X, Gem, Sparkles } from 'lucide-react';
import { metaBuildsApi, entriesApi, PLACEHOLDER_IMAGE } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const MetaBuildManager = () => {
    const { user } = useAuth();
    const [builds, setBuilds] = useState([]);
    const [relics, setRelics] = useState([]);
    const [accessories, setAccessories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingBuild, setEditingBuild] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [buildToDelete, setBuildToDelete] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        relic_ids: [],
        accessory_ids: [],
        talents: []
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [buildsRes, relicsRes, accessoriesRes] = await Promise.all([
                metaBuildsApi.getAll(),
                entriesApi.getAll('relics'),
                entriesApi.getAll('accessories')
            ]);
            setBuilds(buildsRes.data || []);
            const relicsData = relicsRes.data;
            const accessoriesData = accessoriesRes.data;
            setRelics(Array.isArray(relicsData) ? relicsData : (relicsData?.items || []));
            setAccessories(Array.isArray(accessoriesData) ? accessoriesData : (accessoriesData?.items || []));
        } catch (error) {
            console.error('Failed to load data:', error);
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            relic_ids: [],
            accessory_ids: [],
            talents: []
        });
        setEditingBuild(null);
    };

    const openCreateModal = () => {
        resetForm();
        setModalOpen(true);
    };

    const openEditModal = (build) => {
        setEditingBuild(build);
        setFormData({
            title: build.title || '',
            description: build.description || '',
            relic_ids: build.relic_ids || [],
            accessory_ids: build.accessory_ids || [],
            talents: build.talents || []
        });
        setModalOpen(true);
    };

    const handleCreate = async () => {
        if (!formData.title.trim()) {
            toast.error('Titel ist erforderlich');
            return;
        }
        try {
            await metaBuildsApi.create(formData, user?.discord_id, user?.username);
            toast.success('Meta Build erstellt');
            setModalOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            console.error('Failed to create build:', error);
            toast.error('Fehler beim Erstellen');
        }
    };

    const handleUpdate = async () => {
        if (!formData.title.trim() || !editingBuild) return;
        try {
            await metaBuildsApi.update(editingBuild.id, formData, user?.discord_id, user?.username);
            toast.success('Meta Build aktualisiert');
            setModalOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            console.error('Failed to update build:', error);
            toast.error('Fehler beim Aktualisieren');
        }
    };

    const handleDelete = async () => {
        if (!buildToDelete) return;
        try {
            await metaBuildsApi.delete(buildToDelete.id, user?.discord_id, user?.username);
            toast.success('Meta Build gelöscht');
            setDeleteModalOpen(false);
            setBuildToDelete(null);
            loadData();
        } catch (error) {
            console.error('Failed to delete build:', error);
            toast.error('Fehler beim Löschen');
        }
    };

    const addRelic = (relicId) => {
        if (relicId && !formData.relic_ids.includes(relicId)) {
            setFormData(prev => ({
                ...prev,
                relic_ids: [...prev.relic_ids, relicId]
            }));
        }
    };

    const removeRelic = (relicId) => {
        setFormData(prev => ({
            ...prev,
            relic_ids: prev.relic_ids.filter(id => id !== relicId)
        }));
    };

    const addAccessory = (accessoryId) => {
        if (accessoryId && !formData.accessory_ids.includes(accessoryId)) {
            setFormData(prev => ({
                ...prev,
                accessory_ids: [...prev.accessory_ids, accessoryId]
            }));
        }
    };

    const removeAccessory = (accessoryId) => {
        setFormData(prev => ({
            ...prev,
            accessory_ids: prev.accessory_ids.filter(id => id !== accessoryId)
        }));
    };

    const addTalent = () => {
        setFormData(prev => ({
            ...prev,
            talents: [...prev.talents, { level: 1, name: '' }]
        }));
    };

    const updateTalent = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            talents: prev.talents.map((t, i) => 
                i === index ? { ...t, [field]: field === 'level' ? parseInt(value) || 1 : value } : t
            )
        }));
    };

    const removeTalent = (index) => {
        setFormData(prev => ({
            ...prev,
            talents: prev.talents.filter((_, i) => i !== index)
        }));
    };

    const getRelicById = (id) => relics.find(r => r.id === id);
    const getAccessoryById = (id) => accessories.find(a => a.id === id);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rose-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="meta-build-manager">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Crown className="text-rose-400" size={24} />
                    <h2 className="text-xl font-semibold text-white">Meta Builds</h2>
                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 text-xs rounded-full">
                        {builds.length}
                    </span>
                </div>
                <Button 
                    onClick={openCreateModal}
                    className="bg-rose-500 hover:bg-rose-400 text-white"
                    data-testid="create-meta-build-btn"
                >
                    <Plus size={16} className="mr-2" />
                    Neuer Build
                </Button>
            </div>

            {/* Builds List */}
            {builds.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    <Crown size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Keine Meta Builds vorhanden</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {builds.map((build) => (
                        <div 
                            key={build.id}
                            className="flex items-center gap-4 p-4 bg-black/40 rounded-lg border border-white/10 hover:border-white/20 transition-colors"
                            data-testid={`meta-build-item-${build.id}`}
                        >
                            <GripVertical className="text-gray-600 cursor-grab" size={20} />
                            <div className="flex-1">
                                <h3 className="font-medium text-white">{build.title}</h3>
                                <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                        <Gem size={12} className="text-cyan-400" />
                                        {build.relic_ids?.length || 0} Relics
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Sparkles size={12} className="text-yellow-400" />
                                        {build.accessory_ids?.length || 0} Accessories
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => openEditModal(build)}
                                    className="text-gray-400 hover:text-white"
                                    data-testid={`edit-build-${build.id}`}
                                >
                                    <Pencil size={16} />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => { setBuildToDelete(build); setDeleteModalOpen(true); }}
                                    className="text-gray-400 hover:text-red-400"
                                    data-testid={`delete-build-${build.id}`}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="font-['Rajdhani'] text-2xl text-white">
                            {editingBuild ? 'Meta Build bearbeiten' : 'Neuer Meta Build'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Title */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Titel *</Label>
                            <Input
                                data-testid="meta-build-title"
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="z.B. Best DPS Build"
                                className="bg-black/40 border-white/10"
                            />
                        </div>

                        {/* Accessories Selection (Unlimited) */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-yellow-400 flex items-center gap-2">
                                    <Sparkles size={16} />
                                    Accessories ({formData.accessory_ids.length})
                                </Label>
                            </div>
                            <Select onValueChange={addAccessory}>
                                <SelectTrigger className="bg-black/40 border-white/10">
                                    <SelectValue placeholder="Accessory hinzufügen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {accessories
                                        .filter(a => !formData.accessory_ids.includes(a.id))
                                        .map(accessory => (
                                            <SelectItem key={accessory.id} value={accessory.id}>
                                                {accessory.title} {accessory.rarity && `(${accessory.rarity})`}
                                            </SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                            <div className="flex flex-wrap gap-2">
                                {formData.accessory_ids.map(id => {
                                    const accessory = getAccessoryById(id);
                                    return accessory ? (
                                        <div key={id} className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                                            <img src={accessory.image_url || PLACEHOLDER_IMAGE} alt="" className="w-6 h-6 rounded object-cover" />
                                            <span className="text-sm text-yellow-400">{accessory.title}</span>
                                            <button onClick={() => removeAccessory(id)} className="text-yellow-400 hover:text-red-400">
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ) : null;
                                })}
                            </div>
                        </div>

                        {/* Relics Selection (Unlimited) */}
                        <div className="space-y-3">
                            <Label className="text-cyan-400 flex items-center gap-2">
                                <Gem size={16} />
                                Relics ({formData.relic_ids.length})
                            </Label>
                            <Select onValueChange={addRelic}>
                                <SelectTrigger className="bg-black/40 border-white/10">
                                    <SelectValue placeholder="Relic hinzufügen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {relics
                                        .filter(r => !formData.relic_ids.includes(r.id))
                                        .map(relic => (
                                            <SelectItem key={relic.id} value={relic.id}>
                                                {relic.title} {relic.rarity && `(${relic.rarity})`}
                                            </SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                            <div className="flex flex-wrap gap-2">
                                {formData.relic_ids.map(id => {
                                    const relic = getRelicById(id);
                                    return relic ? (
                                        <div key={id} className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                                            <img src={relic.image_url || PLACEHOLDER_IMAGE} alt="" className="w-6 h-6 rounded object-cover" />
                                            <span className="text-sm text-cyan-400">{relic.title}</span>
                                            <button onClick={() => removeRelic(id)} className="text-cyan-400 hover:text-red-400">
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ) : null;
                                })}
                            </div>
                        </div>

                        {/* Talents */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-purple-400">Talents</Label>
                                <Button 
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={addTalent}
                                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                                >
                                    <Plus size={14} className="mr-1" />
                                    Talent hinzufügen
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {formData.talents.map((talent, idx) => (
                                    <div key={idx} className="flex items-center gap-3">
                                        <Input
                                            type="number"
                                            min="1"
                                            value={talent.level}
                                            onChange={(e) => updateTalent(idx, 'level', e.target.value)}
                                            placeholder="Level"
                                            className="w-20 bg-black/40 border-white/10"
                                        />
                                        <Input
                                            value={talent.name}
                                            onChange={(e) => updateTalent(idx, 'name', e.target.value)}
                                            placeholder="Talent Name"
                                            className="flex-1 bg-black/40 border-white/10"
                                        />
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => removeTalent(idx)}
                                            className="text-red-400 hover:text-red-300"
                                        >
                                            <X size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Beschreibung</Label>
                            <Textarea
                                data-testid="meta-build-description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Build Beschreibung..."
                                rows={4}
                                className="bg-black/40 border-white/10 resize-none"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => { setModalOpen(false); resetForm(); }}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={editingBuild ? handleUpdate : handleCreate}
                            className="bg-rose-500 hover:bg-rose-400 text-white"
                            data-testid="save-meta-build-btn"
                        >
                            {editingBuild ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Meta Build löschen?</DialogTitle>
                    </DialogHeader>
                    <p className="text-gray-400">
                        Möchtest du "{buildToDelete?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                    </p>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-400 text-white"
                            data-testid="confirm-delete-build"
                        >
                            Löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
